import { 
  Box, 
  Typography, 
  Paper,
  Grid,
  Container,
  alpha,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import Sidebar from './sideBar';
import { useDispatch, useSelector } from 'react-redux';
import { allCollections } from '../../state/slices/admin';
import Loading from '../../components/loading/loading';
import { useTranslation } from 'react-i18next';
import { 
  People, 
  School, 
  Assignment, 
  CheckCircle, 
  HourglassEmpty, 
  Cancel,
  TrendingUp,
} from '@mui/icons-material';

function Dashboard() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const allCollection = useSelector(state => state.admin);
  
  useEffect(() => {
    dispatch(allCollections());
  }, [dispatch]);

  const getIcon = (collectionName) => {
    const name = collectionName?.toLowerCase() || '';
    if (name.includes('tutor')) return <School sx={{ fontSize: 40 }} />;
    if (name.includes('student') || name.includes('user')) return <People sx={{ fontSize: 40 }} />;
    if (name.includes('request')) return <Assignment sx={{ fontSize: 40 }} />;
    return <TrendingUp sx={{ fontSize: 40 }} />;
  };

  const getColor = (collectionName) => {
    const name = collectionName?.toLowerCase() || '';
    if (name.includes('tutor')) return theme.palette.primary.main;
    if (name.includes('student') || name.includes('user')) return theme.palette.secondary.main;
    if (name.includes('request')) return '#4caf50';
    return theme.palette.info.main;
  };

  const formatCollectionName = (name) => {
    if (!name) return '';
    // Split by camelCase or add space before capital letters
    const formatted = name
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive capitals
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return formatted;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
      }}
    >
      {allCollection.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Loading />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh' }}>
          <Box
            sx={{
              width: { xs: '100%', md: 280 },
              flexShrink: 0,
              borderRight: { xs: 'none', md: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` },
              borderBottom: { xs: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, md: 'none' },
            }}
          >
            <Sidebar />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              width: { xs: '100%', md: 'calc(100% - 280px)' },
              overflow: 'auto',
              minWidth: 0,
            }}
          >
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 4,
                }}
              >
                {t('dashboard.adminDashboard')}
              </Typography>

              <Grid container spacing={3}>
                {allCollection.collections.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        border: `2px solid ${alpha(getColor(item.collectionName), 0.2)}`,
                        bgcolor: 'white',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: `linear-gradient(90deg, ${getColor(item.collectionName)}, ${alpha(getColor(item.collectionName), 0.6)})`,
                        },
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: `0 12px 40px ${alpha(getColor(item.collectionName), 0.2)}`,
                          borderColor: getColor(item.collectionName),
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            p: 2,
                            borderRadius: '50%',
                            bgcolor: alpha(getColor(item.collectionName), 0.1),
                            mb: 2,
                            color: getColor(item.collectionName),
                          }}
                        >
                          {getIcon(item.collectionName)}
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600, 
                            color: theme.palette.primary.main,
                            mb: 2,
                            fontSize: '1rem',
                          }}
                        >
                          {formatCollectionName(item.collectionName)}
                        </Typography>
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            fontWeight: 700, 
                            color: getColor(item.collectionName),
                            lineHeight: 1.2,
                          }}
                        >
                          {item.collectionSize}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Dashboard;
